import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { updateDeviceInstance } from '../../api/deviceInstances';
import { getRooms } from '../../api/rooms';
import { getElectricalPanels, type ElectricalPanel } from '../../api/electricalPanels';
import type { DeviceInstance } from '../../types';

interface CableInstanceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInstance: DeviceInstance | null;
  projectId: number;
  onSuccess?: () => void; // Callback to trigger data reload
}

export default function CableInstanceEditModal({ 
  isOpen, 
  onClose, 
  selectedInstance, 
  projectId,
  onSuccess 
}: CableInstanceEditModalProps) {
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);
  const [editFromRoom, setEditFromRoom] = useState<string>('');
  const [editToRoom, setEditToRoom] = useState<string>('');
  const [currentInstance, setCurrentInstance] = useState<DeviceInstance | null>(selectedInstance);
  
  const queryClient = useQueryClient();

  // Update currentInstance when selectedInstance changes or modal opens
  useEffect(() => {
    if (isOpen && selectedInstance) {
      // Deep comparison to detect changes
      const selectedKey = JSON.stringify({
        id: selectedInstance.id,
        room_from: selectedInstance.room_from,
        room_to: selectedInstance.room_to,
        description: selectedInstance.description,
        length_meters: selectedInstance.length_meters,
      });
      const currentKey = currentInstance ? JSON.stringify({
        id: currentInstance.id,
        room_from: currentInstance.room_from,
        room_to: currentInstance.room_to,
        description: currentInstance.description,
        length_meters: currentInstance.length_meters,
      }) : '';
      
      if (selectedKey !== currentKey) {
        setCurrentInstance(selectedInstance);
      }
    } else if (!isOpen) {
      // Clear currentInstance when modal closes
      setCurrentInstance(null);
    }
  }, [isOpen, selectedInstance, currentInstance]); // Include currentInstance for comparison

  // Fetch rooms
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms', projectId],
    queryFn: () => getRooms(projectId)
  });

  const { data: electricalPanels = [], isLoading: electricalPanelsLoading } = useQuery({
    queryKey: ['electricalPanels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: isOpen && !!projectId,
  });

  // Update mutation
  const updateInstanceMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<DeviceInstance> }) => 
      updateDeviceInstance(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectDevices', projectId] });
      queryClient.invalidateQueries({ queryKey: ['cable-instances', projectId] });
      queryClient.invalidateQueries({ queryKey: ['availableTerminalCables', projectId] });
      onClose();
      onSuccess?.(); // Call the onSuccess prop
    },
    onError: (error) => {
      console.error('Error updating cable instance:', error);
    }
  });

  // Auto-select room for edit modal based on position label
  const parsePositionLabelEdit = (positionLabel: string) => {
    const match = positionLabel.match(/^[A-ZА-Я]+-(\d+\.?\d*)-\d+$/i);
    if (match) {
      const roomNum = match[1];
      const room = rooms.find(r => r.num === roomNum);
      if (room) {
        setEditToRoom(room.id.toString());
      }
    }
  };

  // Load room info for edit modal
  const loadEditRoomInfo = (instance: DeviceInstance) => {
    // First try to get from database fields
    // Согласно логике: room_from из БД → поле "Куда" (editToRoom), room_to из БД → поле "Откуда" (editFromRoom)
    if (instance.room_from && instance.room_to) {
      setEditFromRoom(instance.room_to.toString());
      setEditToRoom(instance.room_from.toString());
      return;
    }
    
    // Fallback to properties if database fields are not set
    if (instance.properties) {
      try {
        const props = typeof instance.properties === 'string' ? JSON.parse(instance.properties) : instance.properties;
        if (props.from_location && props.to_location) {
          setEditFromRoom(props.to_location.toString());
          setEditToRoom(props.from_location.toString());
          return;
        }
      } catch (e) {
        console.error('Error parsing properties:', e);
      }
    }

    // If no room data in database, try to parse from position_label
    if (instance.position_label) {
      parsePositionLabelEdit(instance.position_label);
    }

    // Set default "Откуда" to "Щит ИСУ" (ID=25) if not set
    if (!instance.room_from && !instance.room_to) {
      setEditFromRoom('25');
    }
  };

  // Load room info when modal opens or instance data changes
  useEffect(() => {
    const instanceToUse = selectedInstance || currentInstance;
    if (isOpen && instanceToUse && rooms.length > 0) {
      // Reset room states before loading new data
      setEditFromRoom('');
      setEditToRoom('');
      loadEditRoomInfo(instanceToUse);
    }
  }, [isOpen, selectedInstance, currentInstance, rooms.length]);

  // Handle form submission
  const handleEditInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!instance) return;

    const formData = new FormData(e.target as HTMLFormElement);

    const electricalPanelIdFromForm = formData.get('electrical_panel_id') as string;
    if (!electricalPanelIdFromForm) {
      alert('Пожалуйста, выберите электрический щит');
      return;
    }
    
    const updates: Partial<DeviceInstance> = {
      position_label: formData.get('position_label') as string,
      description: formData.get('description') as string,
      electrical_panel_id: parseInt(electricalPanelIdFromForm, 10),
      length_meters: parseFloat(formData.get('length') as string) || undefined,
      room_from: editToRoom ? parseInt(editToRoom) : undefined,
      room_to: editFromRoom ? parseInt(editFromRoom) : undefined,
      is_reserve: formData.get('is_reserve') ? 1 : 0,
    };

    updateInstanceMutation.mutate({ id: instance.id, updates });
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Use selectedInstance directly if available, otherwise use currentInstance
  // Use useMemo to ensure instance updates when selectedInstance changes
  const instance = useMemo(() => {
    return selectedInstance || currentInstance;
  }, [selectedInstance, currentInstance]);
  
  if (!isOpen || !instance) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={(e) => setMouseDownTarget(e.target)}
      onMouseUp={(e) => {
        if (e.target === mouseDownTarget && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg w-full max-w-[70rem] mx-4 max-h-[90vh] flex flex-col">
        <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold">
              Редактировать экземпляр кабеля <span className="instance-id">#{instance.id}</span>
            </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          <form onSubmit={handleEditInstance} className="space-y-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Позиционное обозначение
              </label>
              <input
                type="text"
                name="position_label"
                required
                defaultValue={instance.position_label || ''}
                onChange={(e) => parsePositionLabelEdit(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Электрический щит</label>
              {electricalPanelsLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                  Загрузка щитов...
                </div>
              ) : (
                <select
                  name="electrical_panel_id"
                  required
                  defaultValue={instance.electrical_panel_id || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Выберите электрический щит...</option>
                  {(electricalPanels as ElectricalPanel[]).map((panel) => (
                    <option key={panel.id} value={panel.id}>
                      {panel.name} ({panel.proj_id})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Откуда {editFromRoom && `(${rooms.find(r => r.id.toString() === editFromRoom)?.name})`}
              </label>
              <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => setEditFromRoom(room.id.toString())}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      editFromRoom === room.id.toString()
                        ? 'bg-blue-500 !bg-blue-500 !text-white border-blue-500'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                    title={room.name}
                  >
                    {room.num}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Куда {editToRoom && `(${rooms.find(r => r.id.toString() === editToRoom)?.name})`}
              </label>
              <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => setEditToRoom(room.id.toString())}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      editToRoom === room.id.toString()
                        ? 'bg-green-500 !bg-green-500 !text-white border-green-500'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                    title={room.name}
                  >
                    {room.num}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Описание
              </label>
              <input
                type="text"
                name="description"
                defaultValue={instance.description || ''}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Описание кабеля..."
              />
              
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Длина (м)
              </label>
              <input
                type="number"
                name="length"
                step="0.1"
                min="0"
                defaultValue={(() => {
                  // First try to get from database field
                  if (instance.length_meters) {
                    return instance.length_meters.toString();
                  }
                  
                  // Fallback to properties
                  if (instance.properties) {
                    try {
                      return JSON.parse(instance.properties).length || '';
                    } catch {
                      return '';
                    }
                  }
                  
                  return '';
                })()}
                className="w-20 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_reserve"
                  name="is_reserve"
                  defaultChecked={Boolean(instance?.is_reserve)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_reserve" className="ml-2 text-sm font-medium text-gray-700">
                  Резервный
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={updateInstanceMutation.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 